import * as React from "react";
import { ExternalLink, Copy, Package, Clock, Truck, DollarSign, BarChart3, AlertTriangle, Users2, UserRoundCog } from "lucide-react";
import { Link } from "react-router-dom";
import { useAction, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { ENV } from "@/env";
import { InternalMetricCard } from "@/components/internal/shared/InternalMetricCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { useSalesOrdersSummary } from "@/hooks/useOrders";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { formatCurrency } from "@/lib/format";
import { getOrCreateSalesDeviceInstallationId } from "@/lib/sales-device";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { hasPermission } from "@/lib/roles";
import {
  PasswordResetCopyLinkButton,
  PasswordResetEmailButton,
} from "./components/PasswordResetEmailButton";
import { ContractComparison } from "./components/ContractComparison";
import { SalesClientPerformance } from "./components/SalesClientPerformance";


function roleLabel(role?: string) {
  if (!role) return "User";
  if (role === "admin") return "Admin";
  if (role === "sales") return "Sales";
  if (role === "warehouse") return "Warehouse";
  if (role === "logistics") return "Logistics";
  return "User";
}

export default function SalesPage() {
  const [selectedUserId, setSelectedUserId] = React.useState<string>("");
  const [generating, setGenerating] = React.useState(false);

  const { authUserId, userRole } = useAuthIdentity();
  const users = useUsersForImpersonation();
  const orders = useSalesOrdersSummary(authUserId);
  const contractAlerts = useQuery(api.contractWorkflow.getContractProgramAlerts, authUserId ? {} : "skip");
  const generateMagicLinkAction = useAction(api.salesActions.generateMagicLoginLink);
  const canViewAnalytics = hasPermission(userRole, "sales:view");
  const canCreateContracts = hasPermission(userRole, "contracts:approve");
  const canManageCustomerAccess = hasPermission(userRole, "sales:edit");
  const canRecoverCustomerAccounts = hasPermission(userRole, "admin:manage");

  const orderMetrics = React.useMemo(() => {
    if (!orders) return null;
    const totalOrders = orders.length;
    const pending = orders.filter((o: any) => o.status === "awaiting_inventory" || o.status === "ready_to_prepare").length;
    const inTransit = orders.filter((o: any) => o.status === "in_transit").length;
    const revenue = orders.reduce((sum: number, o: any) => sum + (o.totalAmount || 0), 0);
    return { totalOrders, pending, inTransit, revenue };
  }, [orders]);

  const selectedUser = React.useMemo(
    () => users?.find((user: any) => user._id === selectedUserId) ?? null,
    [users, selectedUserId],
  );

  const userOptions = React.useMemo(() => {
    return buildCustomerComboboxOptions(users as any[] | undefined);
  }, [users]);

  const generateAndOpen = React.useCallback(async () => {
    if (!selectedUser?.email) {
      toast.error("Please select a user first");
      return;
    }

    const isDev = ENV.IS_DEV;
    let pendingTab: Window | null = null;
    if (!isDev) {
      // Safari blocks async popups unless the tab is created in the click handler.
      pendingTab = window.open("about:blank", "_blank");
      if (!pendingTab) {
        toast.error("Unable to open a new tab. Please allow pop-ups for Anthem Internal.");
        return;
      }
      pendingTab.opener = null;
    }

    setGenerating(true);
    try {
      const result = await generateMagicLinkAction({
        targetEmail: selectedUser.email,
        siteUrl: ENV.SITE_URL || undefined,
        deviceInstallationId: getOrCreateSalesDeviceInstallationId(),
      });
      if (!result.success || !result.url) {
        pendingTab?.close();
        toast.error("Failed to generate magic link");
        return;
      }

      if (isDev) {
        await navigator.clipboard.writeText(result.url);
        toast.success("Magic link copied to clipboard");
      } else if (pendingTab) {
        pendingTab.location.replace(result.url);
        toast.success("Magic link opened in new tab");
      } else {
        toast.error("Unable to open a new tab. Please allow pop-ups for Anthem Internal.");
        return;
      }

      setSelectedUserId("");
    } catch (error) {
      pendingTab?.close();
      toast.error(error instanceof Error ? error.message : "Failed to generate magic link");
    } finally {
      setGenerating(false);
    }
  }, [selectedUser, generateMagicLinkAction]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Sales Dashboard</h1>
          <p className="text-muted-foreground">Manage customer relationships, contract flow, and sample handoffs.</p>
        </div>
        <div className="flex items-center gap-2">
          {canViewAnalytics ? (
            <Button asChild variant="outline">
              <Link to="/sales/analytics">
                <BarChart3 className="mr-2 h-4 w-4" />
                Analytics
              </Link>
            </Button>
          ) : null}
          <Button asChild variant="outline">
            <Link to="/sales/crm/leads">
              <Users2 className="mr-2 h-4 w-4" />
              Leads
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/sales/orders">Orders</Link>
          </Button>
          {canCreateContracts ? (
            <Button asChild>
              <Link to="/sales/contracts/new">Create Contract</Link>
            </Button>
          ) : null}
        </div>
      </div>

      {canViewAnalytics ? <ContractComparison customers={buildCustomerComboboxOptions(users?.filter((user: { role?: string }) => user.role?.toLowerCase() === "customer"))} /> : null}

      {orderMetrics ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InternalMetricCard
            title="Total Orders"
            value={String(orderMetrics.totalOrders)}
            hint="All sales orders"
            icon={Package}
          />
          <InternalMetricCard
            title="Pending"
            value={String(orderMetrics.pending)}
            hint="Ready to prepare"
            icon={Clock}
          />
          <InternalMetricCard
            title="In Transit"
            value={String(orderMetrics.inTransit)}
            hint="Currently shipping"
            icon={Truck}
          />
          <InternalMetricCard
            title="Revenue"
            value={formatCurrency(orderMetrics.revenue)}
            hint="Total order value"
            icon={DollarSign}
          />
        </div>
      ) : null}

      {contractAlerts &&
      ((contractAlerts.counts?.lateReleases ?? 0) > 0 ||
        (contractAlerts.counts?.defaultedReleases ?? 0) > 0 ||
        (contractAlerts.counts?.nearExpiryContracts ?? 0) > 0) ? (
        <Card className="border-amber-200 bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-900 dark:text-amber-100">
              <AlertTriangle className="h-5 w-5" />
              Contract Program Alerts
            </CardTitle>
            <CardDescription>
              Release defaults, grace-period issues, and contracts nearing the 90-day deadline.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              {(contractAlerts.counts?.lateReleases ?? 0) > 0 ? (
                <span className="rounded-full border border-amber-300 px-3 py-1 text-amber-800 dark:border-amber-800 dark:text-amber-200">
                  {contractAlerts.counts.lateReleases} late release{contractAlerts.counts.lateReleases === 1 ? "" : "s"}
                </span>
              ) : null}
              {(contractAlerts.counts?.defaultedReleases ?? 0) > 0 ? (
                <span className="rounded-full border border-red-300 px-3 py-1 text-red-800 dark:border-red-800 dark:text-red-200">
                  {contractAlerts.counts.defaultedReleases} defaulted release{contractAlerts.counts.defaultedReleases === 1 ? "" : "s"}
                </span>
              ) : null}
              {(contractAlerts.counts?.nearExpiryContracts ?? 0) > 0 ? (
                <span className="rounded-full border border-sky-300 px-3 py-1 text-sky-800 dark:border-sky-800 dark:text-sky-200">
                  {contractAlerts.counts.nearExpiryContracts} contract{contractAlerts.counts.nearExpiryContracts === 1 ? "" : "s"} in final 10 days
                </span>
              ) : null}
            </div>
            <div className="space-y-1">
              {contractAlerts.defaultedReleases?.slice(0, 3).map((alert: any) => (
                <p key={`default-${alert.contractId}-${alert.releaseNumber}`}>
                  <strong>{alert.customerName}</strong>: Contract {alert.contractNumber} release {alert.releaseNumber} defaulted with {alert.remainingBags} bags outstanding.
                </p>
              ))}
              {contractAlerts.lateReleases?.slice(0, 3).map((alert: any) => (
                <p key={`late-${alert.contractId}-${alert.releaseNumber}`}>
                  <strong>{alert.customerName}</strong>: Contract {alert.contractNumber} release {alert.releaseNumber} is in grace with {alert.remainingBags} bags remaining.
                </p>
              ))}
              {contractAlerts.nearExpiryContracts?.slice(0, 3).map((alert: any) => (
                <p key={`expiry-${alert.contractId}`}>
                  <strong>{alert.customerName}</strong>: Contract {alert.contractNumber} ends {alert.endDateLabel} with {alert.remainingBags} bags still open.
                </p>
              ))}
            </div>
            <div>
              <Button asChild variant="outline" size="sm">
                <Link to="/sales/contracts">Review Contracts</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {canManageCustomerAccess ? (
        <Card>
          <CardHeader>
            <CardTitle>Customer Access</CardTitle>
            <CardDescription>
              Open the storefront as a customer or send a password reset email. Orders placed
              through an opened customer session are attributed to you and this device.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Select User</Label>
                <ComboboxSelect
                  options={userOptions}
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                  placeholder="Search and select a user..."
                  searchPlaceholder="Search by company, name, or email..."
                  emptyText="No users found."
                  loading={users === undefined}
                />
                {selectedUser ? (
                  <p className="text-muted-foreground text-xs">
                    Email: {selectedUser.email} &bull; Role: {roleLabel(selectedUser.role)}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label>Customer Access</Label>
                <div className="grid gap-2">
                  <Button onClick={() => void generateAndOpen()} className="w-full" disabled={!selectedUserId || generating}>
                    {ENV.IS_DEV ? <Copy className="mr-2 h-4 w-4" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                    {generating ? (ENV.IS_DEV ? "Copying..." : "Opening...") : (ENV.IS_DEV ? "Copy Login Link" : "Open as Customer")}
                  </Button>
                  <PasswordResetEmailButton
                    email={selectedUser?.email}
                    variant="outline"
                    className="w-full"
                    disabled={!selectedUserId}
                  />
                  <PasswordResetCopyLinkButton
                    email={selectedUser?.email}
                    variant="outline"
                    className="w-full"
                    disabled={!selectedUserId}
                  />
                </div>
              </div>
            </div>
            {canRecoverCustomerAccounts ? (
              <div className="flex flex-col gap-3 rounded-lg border border-dashed bg-muted/20 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">Customer not listed?</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Incomplete signups do not appear in this customer list. Search the login
                    account, staged signup details, and verification email history by exact email.
                  </p>
                </div>
                <Button asChild variant="outline" className="shrink-0">
                  <Link to="/admin?tab=recovery#account-recovery">
                    <UserRoundCog className="mr-2 h-4 w-4" />
                    Open account recovery
                  </Link>
                </Button>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <SalesClientPerformance />
    </div>
  );
}
